import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useTracking } from '@xing-com/crate-communication-tracking';
import ErrorStatusBanner from '@xing-com/crate-following-status-banner';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';

import {
  recommendationPageToTrackingPageMap,
  RecommendationPageType,
} from '../../config';
import { useRedirectToLoginWithDestinationUrl } from '../../hooks/use-redirect-to-login-with-destination-url';
import { useSetFollowStatus } from '../../hooks/use-set-follow-status';
import type { RecommendationCardItem } from '../../types/types';
import { FollowButton } from '../follow-button/follow-button';

import * as Styled from './card-item.styles';

const TIME_TO_CLOSE_ERROR_BANNER = 3000;

export const CardItem: React.FC<RecommendationCardItem> = ({
  id,
  url,
  imageSrc,
  headline,
  description,
  isFollowed,
  pageType,
  urn,
}) => {
  const isInsiderRecommendations = pageType === RecommendationPageType.INSIDER;
  const { track: trackNwt } = useTracking();
  const { isFollowing, isUpdating, hasError, toggleFollow, resetError } =
    useSetFollowStatus(id, isFollowed, pageType);
  const { isLoggedIn } = useLoginState();
  const redirectToLoginWithDestinationUrl =
    useRedirectToLoginWithDestinationUrl();
  const intl = useIntl();

  const handleFollowClick = useCallback((): void => {
    if (isLoggedIn) {
      trackNwt({
        type: 'nwt',
        event: isFollowing ? 'unfollowed' : 'followed',
        application: 'wbm',
        eventSchema: 'basic',
        sentBy: 'content',
        page: `following/recommendations/${recommendationPageToTrackingPageMap[pageType]}`,
        itemUrn: urn,
      });

      toggleFollow();
    } else {
      redirectToLoginWithDestinationUrl();
    }
  }, [
    trackNwt,
    isFollowing,
    pageType,
    urn,
    toggleFollow,
    isLoggedIn,
    redirectToLoginWithDestinationUrl,
  ]);

  return (
    <Styled.CardContainer variant="elevated" data-testid="recommendation-card">
      <Styled.ImageContainer>
        <Styled.CardLink
          href={url}
          data-testid="recommendation-card-image-link"
          aria-hidden="true"
          tabIndex={-1}
        >
          <Styled.SuperEllipse size="large" src={imageSrc} alt="" />
        </Styled.CardLink>
      </Styled.ImageContainer>
      <Styled.CardContent>
        <Styled.CardLink
          href={url}
          data-testid="recommendation-card-content-link"
          aria-label={intl.formatMessage(
            {
              id: 'NEWS_SOURCES_ARIA_LABEL_GO_TO_OVERVIEW',
            },
            { overviewPage: headline }
          )}
        >
          <Styled.Headline
            size="large"
            $showMoreThanOneLine={!isInsiderRecommendations}
          >
            {headline}
          </Styled.Headline>
          <Styled.BodyCopy $showMoreThanOneLine={isInsiderRecommendations}>
            {description}
          </Styled.BodyCopy>
        </Styled.CardLink>
        <Styled.ButtonContainer>
          <FollowButton
            isLoading={isUpdating}
            isFollowing={isFollowing}
            handleFollowClick={handleFollowClick}
            headline={headline}
          />
        </Styled.ButtonContainer>
      </Styled.CardContent>
      <ErrorStatusBanner
        show={hasError}
        variant="error"
        timeout={TIME_TO_CLOSE_ERROR_BANNER}
        data-qa="recommendation-page-error-banner"
        handleOnClose={resetError}
        showCloseButton={false}
      >
        <FormattedMessage id="CONTENT_NEWS_SOURCES_DEFAULT_ERROR_MESSAGE" />
      </ErrorStatusBanner>
    </Styled.CardContainer>
  );
};
